/**
 * Sprint 11 — In-app notifications, DB-backed via /api/v1/notifications.
 *
 * Each row carries a `deeplink` (set by the server-side trigger) which we use
 * as the click target. Tabs filter `unread_only`.
 */
import React from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { v1Get, v1Post } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CheckCheck, Bell, AlertTriangle, CheckCircle2, Eye, CalendarClock,
  FileText, XCircle, Sparkles, Mail, Info,
} from 'lucide-react';
import { classNames, fmtDateTimeIST } from '@/lib/format';
import { toast } from 'sonner';
import { DataTablePagination, usePagedQueryParams } from '@/components/DataTablePagination';

/** Longest hire tokens first so `no_hire` is not split by a later `hire` replace. */
const HIRE_REC_BODY_REPLACEMENTS = [
  [/strong_no_hire/gi, 'Not recommended'],
  [/strong no hire/gi, 'Not recommended'],
  [/strong_hire/gi, 'Strong Recommend'],
  [/strong hire/gi, 'Strong Recommend'],
  [/lean_hire/gi, 'Recommend'],
  [/lean hire/gi, 'Recommend'],
  [/no_hire/gi, 'Not recommended'],
  [/no hire/gi, 'Not recommended'],
  [/\bhire\b/gi, 'Recommend'],
];

/** Panel AI scores use the rubric's 0-10 scale; rows written before the fix say `/ 5`. */
const LEGACY_SCORE_SCALE_REPLACEMENTS = [
  [/(was scored (?:\d+(?:\.\d+)?|N\/A)) \/ 5\b/gi, '$1 / 10'],
];

function formatNotificationBody(body) {
  if (!body) return body;
  return [...HIRE_REC_BODY_REPLACEMENTS, ...LEGACY_SCORE_SCALE_REPLACEMENTS]
    .reduce((out, [re, label]) => out.replace(re, label), body);
}

/** First email in body — fallback for older `user_invited` rows without `?q=`. */
const EMAIL_IN_TEXT = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

/** Title prefix → Candidate Detail tab for older stage-completion notifs without `?tab=`. */
const STAGE_COMPLETION_TITLE_TO_TAB = [
  ['IQ Assessment completed', 'mcq'],
  ['Video Bot completed', 'videobot'],
  ['Tech Assessment completed', 'tech-assessment'],
];

/** Build SPA path from deeplink; enrich invite / stage-completion query params. */
function resolveNotificationNavigateTarget(n) {
  if (!n?.deeplink) return null;
  let path;
  let search = '';
  let hash = '';
  if (n.deeplink.startsWith('http')) {
    const url = new URL(n.deeplink);
    path = url.pathname;
    search = url.search;
    hash = url.hash;
  } else {
    const hashIdx = n.deeplink.indexOf('#');
    const withoutHash = hashIdx >= 0 ? n.deeplink.slice(0, hashIdx) : n.deeplink;
    hash = hashIdx >= 0 ? n.deeplink.slice(hashIdx) : '';
    const qIdx = withoutHash.indexOf('?');
    path = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
    search = qIdx >= 0 ? withoutHash.slice(qIdx) : '';
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  let changed = false;

  if (n.kind === 'user_invited' && !params.get('q')) {
    const match = (n.body || '').match(EMAIL_IN_TEXT);
    if (match) {
      params.set('q', match[0]);
      changed = true;
    }
  }

  if (n.kind === 'assigned_hr_stage_completed') {
    if (!params.get('eval') && params.get('evaluation_id')) {
      params.set('eval', params.get('evaluation_id'));
      params.delete('evaluation_id');
      changed = true;
    }
    if (!params.get('tab')) {
      const title = n.title || '';
      for (const [prefix, tab] of STAGE_COMPLETION_TITLE_TO_TAB) {
        if (title.startsWith(prefix)) {
          params.set('tab', tab);
          changed = true;
          break;
        }
      }
    }
  }

  if (changed || n.kind === 'user_invited' || n.kind === 'assigned_hr_stage_completed') {
    const qs = params.toString();
    search = qs ? `?${qs}` : '';
  }
  return path + search + hash;
}

const KIND_ICON = {
  panel_evaluation_ready: Sparkles,
  candidate_otc_link: Mail,
  client_share_link: Eye,
  client_share_otc_code: ShieldIcon,
};

function ShieldIcon(props) { return <CheckCircle2 {...props} />; }

const SEVERITY_TONE = {
  info: 'bg-sky-50 text-sky-700 border-sky-200',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  error: 'bg-rose-50 text-rose-700 border-rose-200',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return fmtDateTimeIST(iso, { dateStyle: 'medium', timeStyle: 'short' }, iso);
  } catch { return iso; }
};

export default function NotificationsCenter() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { params, setPage, setPageSize, setFilter } =
    usePagedQueryParams({ initial: { filter: 'all' } });
  const filter = params.filter || 'all';

  const qs = new URLSearchParams();
  qs.set('paginated', 'true');
  qs.set('page', params.page);
  qs.set('page_size', params.page_size);
  if (filter === 'unread') qs.set('unread_only', 'true');

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['v1-notifications', qs.toString()],
    queryFn: () => v1Get(`/notifications?${qs.toString()}`),
    keepPreviousData: true,
  });
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 0;
  const unread = data?.unread_count ?? 0;

  const markAllMut = useMutation({
    mutationFn: () => v1Post('/notifications/mark-read', { all: true }),
    onSuccess: (res) => {
      toast.success(`Marked ${res.marked} as read`);
      qc.invalidateQueries({ queryKey: ['v1-notifications'] });
    },
  });
  const markOneMut = useMutation({
    mutationFn: (id) => v1Post('/notifications/mark-read', { ids: [id] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['v1-notifications'] }),
  });

  const handleOpen = (n) => {
    if (!n.read_at) markOneMut.mutate(n.id);
    // Absolute deeplinks → SPA path; user_invited always carries/ensures ?q=email.
    const target = resolveNotificationNavigateTarget(n);
    if (target) navigate(target);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        subtitle={`${unread} unread · Real-time activity from HireIQ`}
        actions={
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={() => markAllMut.mutate()}
            disabled={markAllMut.isPending || unread === 0}
            data-testid="notifications-mark-all-read-button"
          >
            <CheckCheck className="h-4 w-4" /> Mark all read
          </Button>
        }
      />
      <Tabs value={filter} onValueChange={(v) => setFilter('filter', v)}>
        <TabsList>
          <TabsTrigger value="all" data-testid="notif-filter-all">All</TabsTrigger>
          <TabsTrigger value="unread" data-testid="notif-filter-unread">Unread</TabsTrigger>
        </TabsList>
      </Tabs>
      <Card className="card-elevated">
        <CardContent className="p-0">
          <ul className="divide-y" data-testid="notifications-list">
            {isLoading && (
              <li className="p-8 text-center text-sm text-muted-foreground">Loading…</li>
            )}
            {!isLoading && items.length === 0 && (
              <li className="p-8 text-center text-sm text-muted-foreground">
                {filter === 'unread' ? 'No unread notifications.' : 'All caught up.'}
              </li>
            )}
            {items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              const tone = SEVERITY_TONE[n.severity] || SEVERITY_TONE.info;
              return (
                <li key={n.id}>
                  <button
                    data-testid="notification-item"
                    onClick={() => handleOpen(n)}
                    className={classNames(
                      'flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-accent',
                      !n.read_at && 'bg-primary/[0.02]',
                    )}
                  >
                    <span className={classNames(
                      'grid h-9 w-9 shrink-0 place-items-center rounded-full border',
                      tone,
                    )}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{n.title}</p>
                        {!n.read_at && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                        <Badge variant="outline" className="ml-auto text-[10px]">
                          {n.kind.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      {n.body && (
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {formatNotificationBody(n.body)}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground tabular-nums">{fmt(n.created_at)}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          <DataTablePagination
            page={params.page}
            pageSize={params.page_size}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            isLoading={isFetching}
          />
        </CardContent>
      </Card>
    </div>
  );
}
